/**
 * Overview — the answer to "is everything alright?" in under three seconds.
 *
 * The custody identity is printed as ARITHMETIC: both sides, and the difference stated even when it is
 * zero. The design's first load-bearing decision, and the reason is worth keeping in front of whoever
 * edits this next — a verdict badge trains operators to stop reading figures, and figures are the only
 * thing this console is actually for.
 *
 * Healthy is deliberately quiet: one green line, no wall of ticks. Broken repaints the screen as an alarm
 * with exactly one call to action.
 */
import { Link } from "react-router-dom";
import { api, type Flag, type Overview as OverviewData } from "../lib/api";
import { useLoader } from "../lib/session";
import { dateTimeLong, money, moneyShort, relative } from "../lib/format";
import { ActionButton, C, card, cardPad, CardHead, Chevron, EmptyState, ErrorState, Sev, Skeleton, Tick, Warn } from "../ui/primitives";

const ACCOUNT_LABELS: Record<string, { label: string; sub: string }> = {
  platform_collection: { label: "COLLECTION ACCOUNT", sub: "what we hold at the provider" },
  in_transit_sweep: { label: "IN-TRANSIT SWEEP", sub: "settlement → collection" },
  in_transit_payout: { label: "IN-TRANSIT PAYOUT", sub: "dispatched, awaiting the bank" },
  paystack_settlement: { label: "PAYSTACK SETTLEMENT", sub: "cards awaiting settlement" },
  platform_fees: { label: "FEES EARNED", sub: "income to date" },
};

export function Overview() {
  const ov = useLoader(() => api.overview(), []);
  const fl = useLoader(() => api.flags(), []);

  if (ov.loading) {
    return (
      <div style={cardPad}>
        <Skeleton rows={3} label="Loading balances… figures are never shown until they are real. No zeros as placeholders." />
      </div>
    );
  }
  if (ov.error || !ov.data) {
    return <div style={card}><ErrorState title="Couldn't load the overview." body={ov.error ?? "The engine did not answer."} onRetry={ov.reload} /></div>;
  }

  const d: OverviewData = ov.data;
  const owedToTenants = d.tenantWallets.totalOwed;
  const feesEarned = -(d.platformAccounts.platform_fees ?? 0); // an income account runs negative-raw
  const weOwe = owedToTenants + feesEarned;
  const weHold = ["platform_collection", "in_transit_sweep", "in_transit_payout", "paystack_settlement"]
    .reduce((sum, k) => sum + (d.platformAccounts[k] ?? 0), 0);
  const difference = Math.round((weOwe - weHold) * 100) / 100;
  const holds = d.custodyIdentity.holds && difference === 0;

  const flags: Flag[] = fl.data?.flags ?? [];
  const worst = flags.find((f) => f.severity === 1) ?? null;
  const asOf = dateTimeLong(new Date(), { tz: true });

  return (
    <>
      {/* When the identity fails, the whole screen becomes an alarm with exactly ONE call to action. */}
      {!holds && (
        <div style={{ border: `1.5px solid ${C.danger}`, background: C.dangerSoft, borderRadius: 15, padding: "15px 17px", display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
          <Warn size={20} />
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: C.danger }}>
              The custody identity does not hold.{" "}
              {difference > 0
                ? `We owe ${money(difference)} more than we can see.`
                : `We hold ${money(Math.abs(difference))} more than we owe.`}
            </div>
            <div style={{ fontSize: 12.5, color: "var(--ink2)", marginTop: 3, lineHeight: 1.5 }}>
              Everything else on this screen is secondary until this is worked.
            </div>
          </div>
          <Link to="/reconciliation" style={{ padding: "11px 15px", borderRadius: 11, background: C.danger, color: "#fff", fontSize: 12.5, fontWeight: 800 }}>Work the break</Link>
        </div>
      )}

      {/* The identity, as arithmetic the operator can add up themselves. */}
      <div style={{ ...card, padding: "18px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "baseline" }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15 }}>The custody identity</div>
            <div style={{ fontSize: 11.5, color: "var(--muted)", fontWeight: 600, marginTop: 1 }}>Everything we owe, against everything we hold. If these two lines disagree, something is wrong.</div>
          </div>
          <span className="bp-mono" style={{ fontSize: 11, fontWeight: 600, color: "var(--muted)" }}>as of {asOf}</span>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
          <div style={idRow}>
            <span style={idLabel}>WE OWE</span>
            <span style={idPart}>Owed to tenants <b className="bp-mono">{money(owedToTenants)}</b></span>
            <span style={idPart}>+ fees we've earned <b className="bp-mono">{money(feesEarned)}</b></span>
            <span style={{ ...idPart, marginLeft: "auto" }}>= <b className="bp-mono" style={{ fontSize: 15 }}>{money(weOwe)}</b></span>
          </div>
          <div style={idRow}>
            <span style={idLabel}>WE HOLD</span>
            <span style={idPart}>Collection <b className="bp-mono">{money(d.platformAccounts.platform_collection ?? 0)}</b></span>
            <span style={idPart}>+ in transit <b className="bp-mono">{money((d.platformAccounts.in_transit_sweep ?? 0) + (d.platformAccounts.in_transit_payout ?? 0) + (d.platformAccounts.paystack_settlement ?? 0))}</b></span>
            <span style={{ ...idPart, marginLeft: "auto" }}>= <b className="bp-mono" style={{ fontSize: 15 }}>{money(weHold)}</b></span>
          </div>

          {holds ? (
            <div style={{ display: "flex", gap: 9, alignItems: "center", padding: "10px 14px" }}>
              <Tick />
              <span style={{ fontSize: 13, fontWeight: 700, color: C.good }}>
                Difference <span className="bp-mono">{money(0)}</span> — every naira we hold is accounted for.
              </span>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 9, alignItems: "center", padding: "12px 14px", border: `1px solid ${C.dangerLine}`, background: C.dangerSoft, borderRadius: 12, flexWrap: "wrap" }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: C.danger }}>
                Difference <span className="bp-mono" style={{ fontSize: 16 }}>{money(difference)}</span> —{" "}
                {difference > 0 ? "we owe more than we can see." : "we hold more than we owe."}{" "}
                <Link to="/reconciliation" style={{ color: C.danger, textDecoration: "underline" }}>Open reconciliation</Link>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Platform accounts. Abbreviated in tiles ONLY, with the exact figure on hover. */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))", gap: 10 }}>
        {Object.entries(ACCOUNT_LABELS).map(([kind, meta]) => {
          const raw = d.platformAccounts[kind];
          const value = kind === "platform_fees" ? -(raw ?? 0) : (raw ?? 0);
          return (
            <div key={kind} style={{ ...card, padding: "14px 15px", display: "flex", flexDirection: "column", gap: 5 }}>
              <span style={{ fontSize: 11, fontWeight: 800, color: "var(--muted)", letterSpacing: ".04em" }}>{meta.label}</span>
              {raw === undefined ? (
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--muted)", animation: "bp-shim 1.6s infinite" }}>unavailable — retrying</span>
              ) : (
                <span className="bp-mono" style={{ fontSize: 17, fontWeight: 700 }} title={money(value)}>{moneyShort(value)}</span>
              )}
              <span style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600 }}>{meta.sub}</span>
            </div>
          );
        })}
        <div style={{ ...card, padding: "14px 15px", display: "flex", flexDirection: "column", gap: 5 }}>
          <span style={{ fontSize: 11, fontWeight: 800, color: "var(--muted)", letterSpacing: ".04em" }}>TENANT WALLETS</span>
          <span className="bp-mono" style={{ fontSize: 17, fontWeight: 700 }}>{d.tenantWallets.count.toLocaleString("en-NG")}</span>
          <span style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600 }}>{money(owedToTenants)} owed in total</span>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))", gap: 12 }}>
        <div style={card}>
          <CardHead title="Needs attention" sub="Everything the console has raised, worst first. Each links to the screen that clears it." />
          {fl.loading ? <Skeleton rows={3} label="Checking for flags…" />
            : flags.length === 0 ? (
              <div style={{ padding: "22px 16px", display: "flex", gap: 11, alignItems: "center" }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: C.goodSoft, display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}><Tick size={17} /></div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 13.5 }}>Nothing needs attention.</div>
                  <div style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600, marginTop: 1 }}>No open breaks, no dead-lettered webhooks, nothing parked, no payouts overdue.</div>
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column" }}>
                {flags.slice(0, 6).map((f) => (
                  <Link key={f.id} to={targetPath(f)} style={{ display: "flex", gap: 10, alignItems: "center", padding: "12px 16px", borderBottom: "1px solid var(--line)", color: "var(--ink)" }}>
                    <Sev level={f.severity} />
                    <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 600, lineHeight: 1.45 }}>{f.title} — {f.statement}</span>
                    <span className="bp-mono" style={{ fontSize: 12, fontWeight: 700, color: "var(--ink2)", whiteSpace: "nowrap" }}>{f.money === null ? "—" : money(f.money)}</span>
                    <Chevron />
                  </Link>
                ))}
                {flags.length > 6 && (
                  <Link to="/flags" style={{ padding: "11px 16px", fontSize: 12, fontWeight: 800 }}>See all {flags.length} flags →</Link>
                )}
              </div>
            )}
        </div>

        <div style={{ ...card, padding: "15px 16px", display: "flex", flexDirection: "column", gap: 11 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 14 }}>Last reconciliation</div>
            <div style={{ fontSize: 11.5, color: "var(--muted)", fontWeight: 600, marginTop: 1 }}>The most recent tie-out against the provider's own numbers.</div>
          </div>
          {d.lastRecon ? (
            <div style={{ display: "flex", gap: 10, alignItems: "center", padding: "11px 13px", background: "var(--subtle)", border: "1px solid var(--line)", borderRadius: 11, flexWrap: "wrap" }}>
              <span style={{ fontSize: 12.5, fontWeight: 800, width: 76, textTransform: "capitalize" }}>{d.lastRecon.providerKey}</span>
              {d.lastRecon.ok
                ? <span style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12, fontWeight: 700, color: C.good }}><Tick size={13} />Passed</span>
                : <span style={{ fontSize: 12, fontWeight: 800, color: C.danger }}>Did not pass</span>}
              <span style={{ fontSize: 11.5, color: "var(--muted)", fontWeight: 600, marginLeft: "auto" }}>
                {dateTimeLong(d.lastRecon.ranAt, { tz: true })} · {relative(d.lastRecon.ranAt)}
              </span>
            </div>
          ) : (
            <EmptyState title="No tie-out has run yet." body="Reconciliation runs daily once a provider is configured. Until then the ledger is internally consistent but unverified against anyone else's numbers." />
          )}
          <Link to="/reconciliation" style={{ fontSize: 12, fontWeight: 800 }}>Open reconciliation →</Link>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 10 }}>
        <QueueTile label="Dead-lettered webhooks" n={d.attention.deadLetters} to="/queues" />
        <QueueTile label="Parked outbox events" n={d.attention.parkedOutbox} to="/queues" />
        <QueueTile label="Open recon breaks" n={d.attention.openBreaks} to="/reconciliation" />
        <QueueTile label="Payouts in flight" n={d.attention.payoutsInFlight} to="/movements" neutral />
      </div>
    </>
  );
}

function QueueTile({ label, n, to, neutral }: { label: string; n: number; to: string; neutral?: boolean }) {
  const bad = !neutral && n > 0;
  return (
    <Link to={to} style={{ ...card, padding: "13px 15px", display: "flex", flexDirection: "column", gap: 4, color: "var(--ink)", borderColor: bad ? C.dangerLine : "var(--line)" }}>
      <span style={{ fontSize: 11, fontWeight: 800, color: "var(--muted)", letterSpacing: ".04em" }}>{label.toUpperCase()}</span>
      <span className="bp-mono" style={{ fontSize: 20, fontWeight: 700, color: bad ? C.danger : "var(--ink)" }}>{n}</span>
    </Link>
  );
}

export function targetPath(f: Flag): string {
  switch (f.target) {
    case "recon": return "/reconciliation";
    case "queues": return "/queues";
    case "fees": return "/fees";
    case "wallets": return f.targetId ? `/wallets/${encodeURIComponent(f.targetId)}` : "/wallets";
    case "movements": return f.targetId ? `/movements?refId=${encodeURIComponent(f.targetId)}` : "/movements";
    default: return "/";
  }
}

const idRow: React.CSSProperties = { display: "flex", flexWrap: "wrap", gap: "8px 18px", alignItems: "baseline", padding: "12px 14px", background: "var(--subtle)", border: "1px solid var(--line)", borderRadius: 12 };
const idLabel: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: "var(--muted)", width: 130 };
const idPart: React.CSSProperties = { fontSize: 13, color: "var(--ink2)" };
